import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { checkoutTotal, deliveryFeeFor, loyaltyDiscountFor } from "@/lib/cartPricing";
import { doughBurst } from "@/lib/confetti";
import { loadRazorpay } from "@/lib/razorpay";
import { waLink } from "@/lib/contact";
import { toast } from "sonner";
import { useBatch } from "@/context/BatchContext";
import { useStoreStatus } from "@/context/StoreContext";

const UPSELL_IDS = ["dip-cheese-garlic", "garlic-focaccia", "cold-brew"];
const EMPTY_FORM = { name: "", phone: "", fulfilment: "delivery", address: "", area: "Gotri", slot: "ASAP", notes: "", payment_method: "cod", email: "", whatsapp_opt_in: false, batch_alerts_opt_in: false };

export const isValidIndianMobile = (phone) => {
  const digits = (phone || "").replace(/\D/g, "");
  return /^[6-9]\d{9}$/.test(digits) && !/^(\d)\1{9}$/.test(digits);
};

export function useCheckout({ items, subtotal, user, clear, open, checkoutIntent, setCheckoutIntent }) {
  const { refresh: refreshBatch } = useBatch();
  const { refresh: refreshStore } = useStoreStatus();
  const [step, setStep] = useState("cart");
  const [mode, setMode] = useState("standard");
  const [upsell, setUpsell] = useState([]);
  const [placedOrder, setPlacedOrder] = useState(null);
  const [placing, setPlacing] = useState(false);
  const [payEnabled, setPayEnabled] = useState(false);
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [menuAll, setMenuAll] = useState([]);
  const [loyalty, setLoyalty] = useState({ orders_completed: 0, ready_redemptions: 0, next_reward_at: 10 });
  const [redeem, setRedeem] = useState(false);
  const [voucherCode, setVoucherCode] = useState("");

  // Pick up voucher from QR scan (sessionStorage)
  useEffect(() => {
    const stored = sessionStorage.getItem("biga_voucher");
    if (stored) {
      setVoucherCode(stored);
      sessionStorage.removeItem("biga_voucher");
    }
  }, [step]); // re-check when cart step changes (e.g. cart opens)
  const [voucherData, setVoucherData] = useState(null); // validated voucher
  const [voucherError, setVoucherError] = useState("");
  const [voucherPick, setVoucherPick] = useState([]); // cart line indexes chosen as free items
  const [form, setForm] = useState(EMPTY_FORM);

  useEffect(() => {
    api.get("/menu").then((response) => {
      setMenuAll(response.data);
      setUpsell(response.data.filter((item) => UPSELL_IDS.includes(item.id)));
    }).catch(() => {});
    api.get("/payments/config").then((response) => setPayEnabled(response.data.enabled)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!user) return;
    api.get("/addresses").then((response) => {
      setSavedAddresses(response.data);
      const defaultAddress = response.data.find((address) => address.is_default) || response.data[0];
      if (defaultAddress) setForm((current) => ({ ...current, address: current.address || `${defaultAddress.line1}, ${defaultAddress.area}, ${defaultAddress.city} ${defaultAddress.pincode}`, area: defaultAddress.area || current.area }));
    }).catch(() => {});
    api.get("/loyalty/me").then((response) => setLoyalty(response.data)).catch(() => {});
  }, [user]);

  useEffect(() => {
    if (user) setForm((current) => ({ ...current, name: current.name || user.name, phone: current.phone || user.phone || "", email: current.email || user.email || "" }));
  }, [user]);

  useEffect(() => {
    if (open && checkoutIntent === "whatsapp") {
      if (items.length > 0) { setMode("whatsapp"); setStep("details"); }
      setCheckoutIntent(null);
    }
  }, [open, checkoutIntent, items.length, setCheckoutIntent]);

  const deliveryFee = deliveryFeeFor(form.fulfilment, subtotal);
  const loyaltyDiscount = useMemo(() => loyaltyDiscountFor({ items, menuItems: menuAll, redeem, readyRedemptions: loyalty.ready_redemptions }), [items, loyalty.ready_redemptions, menuAll, redeem]);
  const total = checkoutTotal(subtotal, deliveryFee, loyaltyDiscount);
  const itemsKey = items.map((item) => `${item.key || item.item_id}:${item.qty}`).join("|");
  const voucherDeduction = useMemo(() => {
    if (!voucherData) return 0;
    if (voucherData.type === "value") return Math.min(voucherData.value, total);
    if (voucherData.type === "discount") return voucherData.discount_type === "percentage" ? Math.round(total * voucherData.discount_amount / 100) : Math.min(voucherData.discount_amount, total);
    const picked = (voucherData.eligible || []).filter((line) => voucherPick.includes(line.index));
    return Math.min(total, picked.reduce((sum, line) => sum + line.unit_price, 0));
  }, [voucherData, voucherPick, total]);

  useEffect(() => {
    if (voucherData?.type === "item") { setVoucherData(null); setVoucherPick([]); setVoucherError("Your cart changed. Please apply your voucher again."); }
  }, [itemsKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const togglePick = (line) => {
    const limit = (voucherData?.categories || []).filter((c) => c.category === line.category).reduce((sum, c) => sum + c.qty, 0);
    setVoucherPick((current) => {
      if (current.includes(line.index)) return current.filter((i) => i !== line.index);
      const sameCategory = current.filter((i) => voucherData.eligible.find((e) => e.index === i)?.category === line.category);
      const kept = sameCategory.length >= limit ? current.filter((i) => i !== sameCategory[0]) : current;
      return [...kept, line.index];
    });
  };

  const upsellToShow = useMemo(() => {
    const cartItemIds = new Set(items.map((item) => item.item_id));
    return upsell.filter((item) => !cartItemIds.has(item.id)).slice(0, 2);
  }, [items, upsell]);

  const finalizeOrder = (order) => {
    setPlacedOrder(order);
    setStep("done");
    clear();
    refreshBatch();
    doughBurst(window.innerWidth * 0.8, window.innerHeight * 0.4);
    setTimeout(() => doughBurst(window.innerWidth * 0.85, window.innerHeight * 0.5), 220);
  };

  const buildPayload = (paymentMethod, source) => ({
    items: items.map((item) => ({ item_id: item.item_id, name: item.name, size: item.size, crust: item.crust, extras: item.extras, half_and_half: item.half_and_half, qty: item.qty, unit_price: item.unit_price, line_total: item.unit_price * item.qty })),
    customer_name: form.name, phone: form.phone, fulfilment: form.fulfilment, address: form.address || null, area: form.area, slot: form.slot, notes: form.notes || null, payment_method: paymentMethod,
    email: form.email || null, whatsapp_opt_in: form.whatsapp_opt_in, batch_alerts_opt_in: form.batch_alerts_opt_in, subtotal, delivery_fee: deliveryFee, total, redeem_free_pizza: redeem && loyalty.ready_redemptions > 0,
    ...(voucherData ? { voucher_code: voucherData.code } : {}),
    ...(voucherData?.type === "item" ? { voucher_item_indexes: voucherPick } : {}),
    ...(source ? { source } : {}),
  });

  const applyVoucher = async () => {
    if (!voucherCode.trim()) return;
    if (!form.phone.trim()) { setVoucherError("Please enter your phone number first."); return; }
    setVoucherError("");
    try {
      const cartLines = items.map((item) => ({ item_id: item.item_id, name: item.name, unit_price: item.unit_price, qty: item.qty }));
      const { data } = await api.post("/vouchers/validate", { code: voucherCode.trim(), phone: form.phone.trim(), items: cartLines });
      setVoucherData(data);
      setVoucherPick(data.type === "item" && data.eligible?.length === 1 ? [data.eligible[0].index] : []);
      toast.success("Voucher applied!");
    } catch (e) {
      setVoucherError(e.response?.data?.detail || "Invalid voucher");
      setVoucherData(null);
    }
  };

  const clearVoucher = () => { setVoucherCode(""); setVoucherData(null); setVoucherPick([]); setVoucherError(""); };
  const needsFreeItemPick = voucherData?.type === "item" && voucherPick.length === 0;

  const showOrderError = (error) => {
    const detail = error.response?.data?.detail;
    if (voucherData && typeof detail === "string" && /voucher/i.test(detail)) { setVoucherData(null); setVoucherError(detail); }
    if (error.response?.status === 409) { refreshBatch(); refreshStore(); }
    if (error.response?.status === 400) refreshStore();
    toast.error(formatApiError(detail));
  };

  const validateDetails = () => {
    if (!form.name || !form.phone || (form.fulfilment === "delivery" && !form.address)) { toast.error("Please fill all details"); return false; }
    if (!isValidIndianMobile(form.phone)) { toast.error("Enter a valid 10-digit Indian mobile number"); return false; }
    return true;
  };

  const payOnline = async (order) => {
    try {
      const { data } = await api.post("/payments/razorpay/order", { order_id: order.id });
      if (data.payment_required === false) { toast.success("Your loyalty reward covered this order."); finalizeOrder(data.order); return; }
      if (!await loadRazorpay()) { toast.error("Couldn't load the payment window. Please try again."); return; }
      const prefill = { name: form.name, contact: form.phone };
      if (user?.email) prefill.email = user.email;
      const payment = new window.Razorpay({ key: data.key_id, amount: data.amount, currency: data.currency, name: "BIGA Artisanal Pizzeria", description: `Order ${order.order_number}`, order_id: data.razorpay_order_id, prefill, theme: { color: "#FF5003" }, timeout: 1800, retry: { enabled: false }, handler: async (result) => {
        try {
          const verified = await api.post("/payments/razorpay/verify", { order_id: order.id, razorpay_order_id: result.razorpay_order_id, razorpay_payment_id: result.razorpay_payment_id, razorpay_signature: result.razorpay_signature });
          finalizeOrder(verified.data);
        } catch { toast.error("Payment could not be verified. Please contact us."); }
      }, modal: { ondismiss: async () => { try { await api.post(`/orders/${order.id}/cancel-payment`); } catch {} toast.message("Payment cancelled. Any voucher or points you used have been restored."); } } });
      payment.on("payment.failed", () => { toast.error("Payment failed and this order was cancelled. Please place your order again."); });
      payment.open();
    } catch (error) { toast.error(formatApiError(error.response?.data?.detail)); }
  };

  const placeOrder = async () => {
    if (!validateDetails()) return;
    if (needsFreeItemPick) { toast.error("Choose your free item first"); return; }
    const paymentMethod = payEnabled ? form.payment_method : "cod";
    setPlacing(true);
    try {
      const { data } = await api.post("/orders", buildPayload(paymentMethod));
      if (paymentMethod === "online") await payOnline(data); else finalizeOrder(data);
    } catch (error) { showOrderError(error); } finally { setPlacing(false); }
  };

  const placeWhatsAppOrder = async () => {
    if (!validateDetails()) return;
    if (needsFreeItemPick) { toast.error("Choose your free item first"); return; }
    setPlacing(true);
    try {
      const { data } = await api.post("/orders", buildPayload("cod", "whatsapp"));
      let message = `🍕 *NEW ORDER - BIGA PIZZERIA* 🍕\n\n*Order:* ${data.order_number}\n*Name:* ${form.name}\n*Phone:* ${form.phone}\n`;
      message += form.fulfilment === "delivery" ? `*Delivery to:* ${form.address} (${form.area})\n` : "*Pickup* at BIGA, Gotri Sevasi Road\n";
      message += `*When:* ${form.slot}\n\n`;
      items.forEach((item, index) => { message += `${index + 1}. *${item.name}*${item.size ? ` (${item.size})` : ""} x ${item.qty} - ₹${item.unit_price * item.qty}\n`; if (item.extras?.length) message += `   Extras: ${item.extras.join(", ")}\n`; if (item.half_and_half) message += `   Half & half: ${item.half_and_half}\n`; });
      message += `\n*Subtotal:* ₹${subtotal}${loyaltyDiscount > 0 ? `\n*Biga Circle:* -₹${loyaltyDiscount}` : ""}\n*${form.fulfilment === "delivery" ? "Delivery" : "Pickup"}:* ${deliveryFee === 0 ? "FREE" : `₹${deliveryFee}`}\n*Total:* ₹${data.total}${form.notes ? `\n*Notes:* ${form.notes}` : ""}\n*Payment:* ${form.fulfilment === "delivery" ? "Cash on delivery" : "Pay at pickup"}\n_Free delivery above ₹799 (flat ₹40 otherwise)._\n\n_Please confirm order acceptance and ETA._`;
      window.open(waLink(message), "_blank");
      finalizeOrder(data);
    } catch (error) { showOrderError(error); } finally { setPlacing(false); }
  };

  return {
    menuAll, step, setStep, mode, setMode, placedOrder, setPlacedOrder, placing, payEnabled, savedAddresses, loyalty, redeem, setRedeem, form, setForm, deliveryFee, loyaltyDiscount, total, upsellToShow, validateDetails, placeOrder, placeWhatsAppOrder, voucherCode, setVoucherCode, voucherData, voucherError, applyVoucher, clearVoucher, voucherDeduction, voucherPick, togglePick };
}